// La tarjeta de Hoteles, con cuatro vistas que se pasan con el dedo.
//
// Decisión de Patricio (23-08): «no haría ranking por mes, pondría ocupación
// hoy en una tarjeta, ocupación próximos 7 días otra tarjeta y ocupación 30
// días otra tarjeta» + «las gráficas que tiene ranking me gustan, que las
// podríamos mantener también en ocupación hoy».
//
// Antes había un bloque de ocupación arriba y el ranking debajo. Eran dos
// cosas separadas contando lo mismo. Ahora es UNA tarjeta con cuatro páginas:
// la misma lista de hoteles, medida y ordenada de cuatro formas. Se lee igual
// en las cuatro —posición, nombre, PMS, gráfica, número—, así que pasar de una
// a otra no obliga a reaprender nada.
//
// 🔴 Todo sale de `api.hoteles()`, que ya trae `occ`, `occ30`, `mes`, `spark` y
// `adr`. Ni una petición nueva: la ocupación de 30 días viaja desde el primer
// día en cada fila y no se estaba usando.
import React, { useState } from 'react';
import { Dimensions, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { type HotelRow } from '@/api/client';
import { C, F, heatColor, HEAT_RAMP } from '@/constants/theme';
import { fmt, fmtDec } from '@/lib/format';
import { Card, Chip, PmsBadge, SectionTitle, Spark } from './ui';

type Vista = {
  k: string;
  t: string;
  sub: string;
  /* El valor por el que se ordena. null = ese hotel no compite en esta vista. */
  valor: (h: HotelRow) => number | null;
  /* Lo que se enseña a la derecha. */
  pinta: (h: HotelRow) => { v: string; l: string; mal?: boolean };
  /* La gráfica: serie de números (chispa) o la rejilla de 30 días. */
  grafica: 'occ30curva' | 'occ30' | 'tabla7' | 'acum';
};

/* Las etiquetas de los 7 próximos días, empezando HOY. La rejilla llega sin
   fechas en esta pantalla (van en `/rejilla`, no en `/hoteles`), así que se
   calculan aquí — es el mismo corte de día que usa el resto de la app. */
const DIAS_CORTOS = ['do', 'lu', 'ma', 'mi', 'ju', 'vi', 'sá'];
const proximos7 = () => {
  const hoy = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Madrid', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
  const base = new Date(hoy + 'T00:00:00Z');
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(base.getTime() + i * 86400000);
    return { dia: DIAS_CORTOS[d.getUTCDay()], num: String(d.getUTCDate()).padStart(2, '0'), hoy: i === 0 };
  });
};

const media = (xs: Array<number | null> | null | undefined, n?: number) => {
  const v = (xs ?? []).slice(0, n ?? undefined).filter((x): x is number => x != null);
  return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
};

const VISTAS: Vista[] = [
  {
    k: 'occ-hoy', t: 'Ocupación hoy', sub: 'quién está más lleno · la curva, los 30 días por delante',
    valor: (h) => h.occ ?? null,
    pinta: (h) => ({
      v: h.occ != null ? `${Math.round(h.occ)}%` : '—',
      /* 🔴 EL NÚMERO PRIMERO, y no era solo estética. Ponía «30 d por delante 94 %»
         en una columna de 80 pt con una sola línea: se cortaba en «30 d por
         dela…» y la media —lo único que la etiqueta venía a decir— NO SE VEÍA
         NUNCA. Se lo encontré a Patricio ampliando un fotograma de su vídeo.
         Con el dato delante, si algún día vuelve a recortarse, se pierde la
         unidad y no la cifra. */
      l: (() => { const m = media(h.occ30, 30); return m != null ? `${Math.round(m)}% · 30 d` : 'sin rejilla'; })(),
      mal: h.occ != null && h.occ > 100.4,
    }),
    grafica: 'occ30curva',
  },
  {
    /* 🔴 Esta vista NO lleva la fila normal. Una semana se mira día a día: con
       una curva resumida no se puede decidir en qué día abrir o cerrar cupo,
       que es justo para lo que sirve. Aquí van los siete números, cada uno con
       su barra de color debajo — el número para decidir y la barra para ver la
       forma sin leer. */
    k: 'occ-7', t: 'Ocupación · 7 días', sub: 'día a día, empezando hoy',
    valor: (h) => media(h.occ30, 7),
    pinta: (h) => { const m = media(h.occ30, 7);
      return { v: m != null ? `${Math.round(m)}%` : '—', l: 'media', 
        mal: (h.occ30 ?? []).slice(0, 7).some((x) => x != null && x > 100.4) };
    },
    grafica: 'tabla7',
  },
  {
    k: 'occ-30', t: 'Ocupación · 30 días', sub: 'dónde se llena y dónde queda hueco · la rejilla, día a día',
    valor: (h) => media(h.occ30, 30),
    pinta: (h) => { const m = media(h.occ30, 30);
      return { v: m != null ? `${Math.round(m)}%` : '—',
        l: h.ob ? `${h.ob} día(s) >100%` : 'sin overbooking', mal: !!h.ob };
    },
    grafica: 'occ30',
  },
  {
    k: 'prod', t: 'Producción del mes', sub: 'el ranking de siempre · la curva, acumulada del mes',
    valor: (h) => h.mes,
    pinta: (h) => ({
      v: `${fmt(Math.round(h.mes / 1e3))} k€`,
      l: h.bud != null ? `${h.bud >= 100 ? '▲' : '▼'} ${fmtDec(h.bud - 100)}% bud`
        : (h.d == null ? 'ayer sin volcar' : `ayer ${fmt(Math.round(h.d / 1e3))} k€`),
      mal: h.d == null,
    }),
    grafica: 'acum',
  },
];

export default function RankingCards({ hoteles, verProd, verOcc, onHotel }: {
  hoteles: HotelRow[];
  verProd: boolean;
  verOcc: boolean;
  onHotel: (h: HotelRow) => void;
}) {
  const [pag, setPag] = useState(0);
  const ancho = Dimensions.get('window').width - 36;

  /* Sin permiso de ocupación no hay tres cuartas partes de las vistas; sin
     producción, la cuarta. Se enseñan solo las que tienen sentido para quien
     mira, en vez de una página vacía con un guion. */
  const vistas = VISTAS.filter((v) => (v.k === 'prod' ? verProd : verOcc));
  if (!vistas.length || !hoteles.length) return null;
  const vista = vistas[Math.min(pag, vistas.length - 1)];

  /* 🔴 CADA GRÁFICA, SOBRE SU PROPIO CONCEPTO (regla de Patricio). Antes las
     cuatro vistas enseñaban la misma chispa de producción de 7 días, así que
     en «ocupación hoy» el dibujo hablaba de euros. Ahora:
       · ocupación hoy  → la curva de ocupación de los 30 días por delante
       · 7 días         → los siete días, uno a uno (la tabla de abajo)
       · 30 días        → la rejilla en miniatura
       · producción     → la curva ACUMULADA del mes
     🔴 Y una advertencia honesta: para «ocupación hoy» pedía la del mes EN
     CURSO, y la ocupación de los días ya pasados NO está en los espejos —
     `noray_ocupacion` es una foto de 30 días HACIA DELANTE, no un histórico.
     Así que la curva es la del mes que viene por delante, que es el dato que
     existe. Cuando haya histórico de ocupación, se cambia aquí. */
  const Grafica = ({ h, vista: v }: { h: HotelRow; vista: Vista }) => {
    if (v.grafica === 'acum') {
      const a = h.acum?.length ? h.acum : (h.spark?.length ? h.spark : []);
      return a.length > 1
        ? <Spark vals={a} width={56} height={22} />
        : <View style={{ width: 56 }} />;
    }
    if (v.grafica === 'occ30curva') {
      const vs = (h.occ30 ?? []).filter((x): x is number => x != null);
      return vs.length > 1
        ? <Spark vals={vs} width={56} height={22} />
        : <View style={{ width: 56 }} />;
    }
    /* 30 días: la rejilla, que es el idioma con el que ya se lee la ocupación
       en el resto de la app. Una chispa de 30 puntos no diría nada. */
    return (
      <View style={{ flexDirection: 'row', width: 62 }}>
        {(h.occ30 ?? []).slice(0, 30).map((x, i) => (
          <View key={i} style={{
            width: 1.6, height: 20, marginRight: 0.4, borderRadius: 1,
            backgroundColor: x == null ? 'rgba(140,200,215,0.10)' : x > 100.4 ? C.bad : heatColor(x),
          }} />
        ))}
      </View>
    );
  };

  return (
    <>
      <View style={s.cab}>
        <Text style={s.t}>{vista.t}</Text>
        <View style={s.pts}>
          {vistas.map((v, i) => <View key={v.k} style={[s.pt, i === pag && s.ptOn]} />)}
        </View>
      </View>
      <Text style={s.sub}>{vista.sub}</Text>

      <ScrollView
        horizontal pagingEnabled showsHorizontalScrollIndicator={false}
        snapToInterval={ancho} decelerationRate="fast"
        onMomentumScrollEnd={(e) => setPag(Math.round(e.nativeEvent.contentOffset.x / ancho))}
      >
        {vistas.map((v) => {
          const filas = [...hoteles].map((h) => ({ h, val: v.valor(h) }))
            .sort((a, b) => (b.val ?? -1) - (a.val ?? -1));

          /* ── la vista de la semana: los siete días, uno a uno ── */
          if (v.grafica === 'tabla7') {
            const cab = proximos7();
            return (
              <View key={v.k} style={{ width: ancho }}>
                <Card style={{ paddingVertical: 10, paddingHorizontal: 11 }}>
                  <View style={s7.cab}>
                    <View style={{ width: 88 }} />
                    {cab.map((d, i) => (
                      <View key={i} style={s7.col}>
                        <Text style={[s7.cabDia, d.hoy && { color: C.aqua }]}>{d.dia}</Text>
                        <Text style={[s7.cabNum, d.hoy && { color: C.aqua }]}>{d.num}</Text>
                      </View>
                    ))}
                    <View style={{ width: 40, alignItems: 'flex-end' }}>
                      <Text style={s7.cabDia}>media</Text>
                    </View>
                  </View>

                  {filas.map(({ h }, i) => {
                    const dias = (h.occ30 ?? []).slice(0, 7);
                    const m = media(h.occ30, 7);
                    return (
                      <Pressable
                        key={h.id ?? h.n}
                        style={[s7.fila, i === filas.length - 1 && { borderBottomWidth: 0 }]}
                        onPress={() => onHotel(h)}
                      >
                        <Text style={s7.nom} numberOfLines={1}>{h.n.replace(/^Ona /i, '')}</Text>
                        {Array.from({ length: 7 }, (_, d) => {
                          const x = dias[d] ?? null;
                          const ob = x != null && x > 100.4;
                          return (
                            <View key={d} style={s7.col}>
                              <Text style={[
                                s7.num,
                                x == null ? { color: C.faint } : ob ? { color: C.bad } : x >= 95 ? { color: C.good } : null,
                                d === 0 && { fontFamily: F.uiBold },
                              ]}>{x == null ? '—' : Math.round(x)}</Text>
                              {/* La barra: la forma de la semana sin tener que
                                  leer los números. Alta = lleno. */}
                              <View style={s7.pista}>
                                <View style={{
                                  height: Math.max(2, Math.min(14, ((x ?? 0) - 55) / 45 * 14)),
                                  width: '100%', borderRadius: 1.5,
                                  backgroundColor: x == null ? 'rgba(140,200,215,0.10)' : ob ? C.bad : heatColor(x),
                                }} />
                              </View>
                            </View>
                          );
                        })}
                        <View style={{ width: 40, alignItems: 'flex-end' }}>
                          <Text style={s7.mediaV}>{m != null ? `${Math.round(m)}%` : '—'}</Text>
                        </View>
                      </Pressable>
                    );
                  })}
                  <Text style={s7.pie}>
                    En % · hoy en aqua · rojo = por encima del 100,4 % · toca un hotel para su ficha
                  </Text>
                </Card>
              </View>
            );
          }

          /* ── los 30 días, como la hoja de «ver los 53 →» ──────────────────
             🔴 Patricio, con las dos capturas delante: «los 30 días de ocupación
             los mostraría como la imagen 2». La imagen 2 es la hoja completa que
             se abre desde la tarjeta de ocupación: nombre, los días que pasan de
             100 y la media, con la rejilla a tamaño legible. Aquí estaba en
             miniatura de 62 px dentro de una fila de ranking, que a 1,6 px por
             día no se lee — era el «quizá mejorar el gráfico de 30» de su lista.

             LO QUE NO SE TOCA: ni el dato, ni la rampa de color, ni el umbral de
             overbooking, ni la tarjeta y la hoja que ya funcionan. Esto solo
             cambia CÓMO se presenta esta página del deslizador. Las medidas están
             repetidas aquí a propósito, en vez de compartir el pintor con la
             hoja: la ocupación está perfecta y no la voy a refactorizar en
             semana de demo para ahorrar diez líneas. */
          if (v.grafica === 'occ30') {
            return (
              <View key={v.k} style={{ width: ancho }}>
                <Card style={{ paddingVertical: 4 }}>
                  {/* 🔴 LA LEYENDA, ARRIBA (Patricio, 26-08: «estaría bueno explicar
                      la línea de colores»). La rampa se explica UNA vez y no fila a
                      fila: de oscuro (con hueco) a claro (lleno), y el rojo aparte
                      porque no es «más lleno», es OTRA cosa — overbooking. Son los
                      mismos seis pasos de heatColor: si algún día cambia la rampa,
                      esto la sigue solo. */}
                  <View style={s30.leyenda}>
                    <Text style={s30.leyTxt}>60%</Text>
                    {HEAT_RAMP.map((c) => (
                      <View key={c} style={[s30.leySw, { backgroundColor: c }]} />
                    ))}
                    <Text style={s30.leyTxt}>100%</Text>
                    <View style={{ width: 10 }} />
                    <View style={[s30.leySw, { backgroundColor: C.bad }]} />
                    <Text style={s30.leyTxt}>overbooking</Text>
                  </View>
                  {filas.map(({ h }, i) => {
                    const med = media(h.occ30, 30);
                    return (
                      <Pressable
                        key={h.id ?? h.n}
                        style={[s30.fila, i === filas.length - 1 && { borderBottomWidth: 0 }]}
                        onPress={() => onHotel(h)}
                      >
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                          <Text style={s30.nom} numberOfLines={1}>{h.n.replace(/^Ona /i, '')}</Text>
                          {/* 🔴 DE QUÉ PMS VIENE, que lo pidió expresamente. Y no es
                              cosmético: Avalon y Navision solo publican reservas, así
                              que su ocupación no se lee igual que la de un hotel de
                              Noray con folio. Sin la etiqueta, 53 hoteles de cuatro
                              sistemas parecen todos el mismo dato. */}
                          <PmsBadge pms={h.pms} />
                          {h.ob ? <Chip kind="bad">{`${h.ob} d >100%`}</Chip> : null}
                          <Text style={s30.media}>{med != null ? `${fmtDec(med)}%` : '—'}</Text>
                        </View>
                        {/* Deslizable en horizontal como en la hoja: 30 celdas
                            legibles no caben en el ancho de un iPhone, y
                            encogerlas hasta que quepan es volver a la miniatura. */}
                        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop: 5 }}>
                          <View style={{ flexDirection: 'row' }}>
                            {(h.occ30 ?? []).slice(0, 30).map((x, d) => (
                              <View key={d} style={{
                                width: 13, height: 17, borderRadius: 4, marginRight: 3,
                                backgroundColor: x == null ? 'rgba(140,200,215,0.07)'
                                  : x > 100.4 ? C.bad : heatColor(x),
                              }} />
                            ))}
                          </View>
                        </ScrollView>
                      </Pressable>
                    );
                  })}
                  <Text style={s7.pie}>
                    Rojo = por encima del 100,4 % · desliza la rejilla · toca un hotel para su ficha.
                    Avalon y Navision solo publican reservas: su ocupación es la de las reservas vigentes.
                  </Text>
                </Card>
              </View>
            );
          }

          return (
            <View key={v.k} style={{ width: ancho }}>
              <Card style={{ paddingVertical: 6 }}>
                {filas.map(({ h }, i) => {
                  const p = v.pinta(h);
                  return (
                    <Pressable
                      key={h.id ?? h.n}
                      style={[s.row, i === filas.length - 1 && { borderBottomWidth: 0 }]}
                      onPress={() => onHotel(h)}
                    >
                      {/* Sin Tortugonas aquí: el premio NO se da sobre el mes en
                          curso — se da sobre el último mes PUBLICADO de CG. */}
                      <Text style={s.pos}>{String(i + 1)}</Text>
                      <View style={{ flex: 1, gap: 3 }}>
                        <Text style={s.name} numberOfLines={1}>{h.n.replace(/^Ona /i, '')}</Text>
                        <PmsBadge pms={h.pms} />
                      </View>
                      <Grafica h={h} vista={v} />
                      <View style={{ alignItems: 'flex-end', width: 80 }}>
                        <Text style={[s.val, p.mal && { color: C.bad }]}>{p.v}</Text>
                        <Text style={[s.lbl, p.mal && { color: C.warn }]} numberOfLines={1}>{p.l}</Text>
                      </View>
                    </Pressable>
                  );
                })}
              </Card>
            </View>
          );
        })}
      </ScrollView>
    </>
  );
}

const s30 = StyleSheet.create({
  leyenda: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingVertical: 8, borderBottomColor: C.line, borderBottomWidth: 1 },
  leyTxt: { fontFamily: F.ui, fontSize: 9.5, color: C.faint },
  leySw: { width: 13, height: 11, borderRadius: 3 },
  fila: { paddingVertical: 9, borderBottomColor: C.line, borderBottomWidth: 1 },
  nom: { fontFamily: F.uiMed, fontSize: 12.5, color: C.ink, flex: 1 },
  media: { fontFamily: F.uiBold, fontSize: 12, color: C.muted, fontVariant: ['tabular-nums'] },
});

const s7 = StyleSheet.create({
  cab: { flexDirection: 'row', alignItems: 'flex-end', marginBottom: 6 },
  col: { flex: 1, alignItems: 'center' },
  cabDia: { fontFamily: F.ui, fontSize: 8, color: C.faint, textTransform: 'uppercase', letterSpacing: 0.4 },
  cabNum: { fontFamily: F.uiSemi, fontSize: 10, color: C.muted },
  fila: {
    flexDirection: 'row', alignItems: 'flex-start', paddingVertical: 8,
    borderBottomWidth: 1, borderBottomColor: C.line,
  },
  nom: { width: 88, fontFamily: F.uiMed, fontSize: 11, color: C.ink, paddingRight: 6, paddingTop: 1 },
  num: { fontFamily: F.uiSemi, fontSize: 11, color: C.ink, fontVariant: ['tabular-nums'] },
  /* La barra crece hacia arriba desde una base fija, para que las siete se
     comparen entre sí de un vistazo. */
  pista: { height: 14, width: 11, justifyContent: 'flex-end', marginTop: 3 },
  mediaV: { fontFamily: F.uiBold, fontSize: 12, color: C.muted, fontVariant: ['tabular-nums'], paddingTop: 1 },
  pie: { fontFamily: F.ui, fontSize: 9.5, color: C.faint, marginTop: 9, lineHeight: 14 },
});

const s = StyleSheet.create({
  cab: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 2 },
  t: { fontFamily: F.displayMed, fontSize: 22, color: C.ink },
  sub: { fontFamily: F.ui, fontSize: 11.5, color: C.muted, marginTop: 1, marginBottom: 10 },
  pts: { flexDirection: 'row', gap: 5 },
  pt: { width: 6, height: 6, borderRadius: 3, backgroundColor: 'rgba(140,200,215,0.25)' },
  ptOn: { width: 17, backgroundColor: C.aqua },
  row: {
    flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 11,
    borderBottomWidth: 1, borderBottomColor: C.line,
  },
  pos: { width: 17, fontFamily: F.uiSemi, fontSize: 12, color: C.faint },
  name: { fontFamily: F.uiSemi, fontSize: 14, color: C.ink },
  val: { fontFamily: F.uiBold, fontSize: 14, color: C.ink, fontVariant: ['tabular-nums'] },
  lbl: { fontFamily: F.ui, fontSize: 10, color: C.faint, marginTop: 2 },
});
